// src/features/dashboard/components/Dashboard.tsx

import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Alert,
  FormControl,
  Select,
  MenuItem,
  Button,
  Paper,
} from '@mui/material';
import { useTranslate, useGetIdentity } from 'react-admin';

// Import components
import DashboardMetrics from './DashboardMetrics';
import AssignmentEfficiencySection from './AssignmentEfficiencySection';
import AvailabilityBar from './AvailabilityBar';
import ChartsSection from './ChartsSection';
import SystemHealthSection from './SystemHealthSection';
import SystemInfoView from './SystemInfoView';

// Import hooks
import { useDashboardData, useDashboardPermissions, useHotelSelection } from '../hooks';

const Dashboard: React.FC = () => {
  const translate = useTranslate();
  const { identity } = useGetIdentity();
  
  // Check if user is superadmin
  const isSuperAdmin = identity?.role === 'SuperAdmin';
  
  // Only allow tab switching for superadmin
  const [activeTab, setActiveTab] = useState<'operational' | 'system'>('operational');
  const [period, setPeriod] = useState('today');

  const { canViewAdvancedMetrics } = useDashboardPermissions();
  const { selectedHotelId, setSelectedHotelId, availableHotels, canSelectHotel: canSelect } = useHotelSelection();
  
  const {
    loading,
    error,
    dashboardSummary,
    usageTrends,
    assignmentEfficiency,
  } = useDashboardData({
    hotelId: selectedHotelId || undefined,
    period,
    autoRefresh: true,
  });

  if (!selectedHotelId && !loading) {
    return null;
  }

  return (
    <Box sx={{ 
      
      py: 2
    }}>
      <Container maxWidth={false} sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
        {/* Header Section */}
        <Paper 
          elevation={0}
          sx={{ 
            mb: 3,
            borderRadius: 2,
            overflow: 'hidden',
            backgroundColor:'transparent'
          }}
        >
          <Box sx={{ 
            p: { xs: 2, sm: 2.5 },
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
            justifyContent: isSuperAdmin ? 'space-between' : 'flex-end',
            alignItems: { xs: 'stretch', sm: 'center' }
          }}>
            {/* Tab Buttons - Solo visible para superadmin */}
            {isSuperAdmin && (
              <Box sx={{ 
                display: 'flex',
                borderRadius: 1.5,
                p: 0.5,
                alignSelf: { xs: 'stretch', sm: 'auto' },
                border: '1px solid #1a8fa8'
              }}>
                <Button
                  size="small"
                  onClick={() => setActiveTab('operational')}
                  sx={{
                    flex: { xs: 1, sm: 'initial' },
                    px: 3,
                    py: 1,
                    borderRadius: 1,
                    textTransform: 'uppercase',
                    fontSize: '12px',
                    fontWeight: 600,
                    letterSpacing: '0.5px',
                    color: activeTab === 'operational' ? 'white' : '#64748b',
                    backgroundColor: activeTab === 'operational' ? '#1fa2bf' : 'transparent',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: activeTab === 'operational' ? '#1a8fa8' : 'rgba(31, 162, 191, 0.08)',
                    }
                  }}
                >
                  Operational Dashboard
                </Button>
                <Button
                  size="small"
                  onClick={() => setActiveTab('system')}
                  sx={{
                    flex: { xs: 1, sm: 'initial' },
                    px: 3,
                    py: 1,
                    borderRadius: 1,
                    textTransform: 'uppercase',
                    fontSize: '12px',
                    fontWeight: 600,
                    letterSpacing: '0.5px',
                    color: activeTab === 'system' ? 'white' : '#64748b',
                    backgroundColor: activeTab === 'system' ? '#1fa2bf' : 'transparent',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: activeTab === 'system' ? '#1a8fa8' : 'rgba(31, 162, 191, 0.08)',
                    }
                  }}
                >
                  System Info
                </Button>
              </Box>
            )}
            
            {/* Hotel Selector */}
            {canSelect && availableHotels.length > 0 && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2,
                minWidth: { xs: '100%', sm: 'auto' }
              }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#64748b',
                    fontWeight: 500,
                    display: { xs: 'none', sm: 'block' }
                  }}
                >
                  Hotel
                </Typography>
                <FormControl 
                  size="small" 
                  sx={{ 
                    minWidth: { xs: '100%', sm: 220 },
                    '& .MuiInputLabel-root': {
                      fontSize: '14px'
                    }
                  }}
                >
                  <Select
                    value={selectedHotelId || ''}
                    onChange={(e) => setSelectedHotelId(e.target.value)}
                    displayEmpty
                    sx={{
                      backgroundColor: '#f8fafc',
                      borderRadius: 1,
                      '& .MuiSelect-select': {
                        py: 1.2,
                        fontSize: '14px'
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#e2e8f0',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#1fa2bf',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#1fa2bf',
                        borderWidth: '2px'
                      }
                    }}
                  >
                    {availableHotels.map((hotel) => (
                      <MenuItem key={hotel.id} value={hotel.id}>
                        {hotel.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}
          </Box>
        </Paper>

        {/* Main Content Area */}
        <Box sx={{ 
          position: 'relative',
          minHeight: 'calc(100vh - 200px)'
        }}>
          {/* Loading State */}
          {loading && (
            <Paper 
              elevation={0}
              sx={{ 
                display: 'flex', 
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: 400,
                borderRadius: 2
              }}
            >
              <Box sx={{ textAlign: 'center' }}>
                <CircularProgress size={48} thickness={3} />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Loading dashboard data...
                </Typography>
              </Box>
            </Paper>
          )}

          {/* Error State */}
          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 2,
                borderRadius: 2,
                '& .MuiAlert-icon': {
                  fontSize: '28px'
                }
              }}
            >
              {error}
            </Alert>
          )}

          {/* Dashboard Content */}
          {!loading && dashboardSummary && (
            <Box sx={{ 
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
              pb: 4
            }}>
              {activeTab === 'operational' || !isSuperAdmin ? (
                <>
                  {/* Main Metrics Row */}
                  <Box sx={{ 
                    animation: 'fadeIn 0.5s ease-in',
                    '@keyframes fadeIn': {
                      from: { opacity: 0, transform: 'translateY(10px)' },
                      to: { opacity: 1, transform: 'translateY(0)' }
                    }
                  }}>
                    <DashboardMetrics data={dashboardSummary} />
                  </Box>

                  {/* Assignment Efficiency Section */}
                  {assignmentEfficiency && (
                    <Box sx={{ 
                      animation: 'fadeIn 0.5s ease-in 0.1s both',
                      '@keyframes fadeIn': {
                        from: { opacity: 0, transform: 'translateY(10px)' },
                        to: { opacity: 1, transform: 'translateY(0)' }
                      }
                    }}>
                      <AssignmentEfficiencySection data={assignmentEfficiency} />
                    </Box>
                  )}

                  {/* Average Availability Bar */}
                  <Box sx={{ 
                    animation: 'fadeIn 0.5s ease-in 0.2s both',
                    '@keyframes fadeIn': {
                      from: { opacity: 0, transform: 'translateY(10px)' },
                      to: { opacity: 1, transform: 'translateY(0)' }
                    }
                  }}>
                    <AvailabilityBar data={dashboardSummary} />
                  </Box>

                  {/* Charts Section */}
                  <Box sx={{ 
                    animation: 'fadeIn 0.5s ease-in 0.3s both',
                    '@keyframes fadeIn': {
                      from: { opacity: 0, transform: 'translateY(10px)' },
                      to: { opacity: 1, transform: 'translateY(0)' }
                    }
                  }}>
                    <ChartsSection 
                      usageTrends={usageTrends}
                      dashboardSummary={dashboardSummary}
                    />
                  </Box>

                  {/* System Health Section */}
                  <Box sx={{ 
                    animation: 'fadeIn 0.5s ease-in 0.4s both',
                    '@keyframes fadeIn': {
                      from: { opacity: 0, transform: 'translateY(10px)' },
                      to: { opacity: 1, transform: 'translateY(0)' }
                    }
                  }}>
                    <SystemHealthSection data={dashboardSummary} />
                  </Box>
                </>
              ) : (
                <Box sx={{ 
                  animation: 'fadeIn 0.5s ease-in',
                  '@keyframes fadeIn': {
                    from: { opacity: 0, transform: 'translateY(10px)' },
                    to: { opacity: 1, transform: 'translateY(0)' }
                  }
                }}>
                  <SystemInfoView />
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default Dashboard;